
import { Component, Prop, Vue, Watch } from "vue-property-decorator";
import { cloneDeep, uniq } from "lodash";
import { Message, MessageBox } from "element-ui";
import { packCustomsPacking } from "@/api/hengan-loading-plan";
import { postContainerNumber } from "@/api/customs-packing";
import { CustomsPackingModel, HqPackingListModel } from "@/model/HenganLoadingPlanModels";
import KeywordSearch from "@/components/KeywordSearch/index.vue";
import { highlightKeywords, matchesAllKeywords } from "@/utils/keyword-search";

interface PackingProduct {
  description: string;
  declarationDescription: string;
  guestModel: string;
  hqOrJrNumber: string;
  oeNumber: string;
  refNumber: string;
  hqTransactionNumber: string;
  totalNumberPcs: number;
  purchaseOrderNumber: string;
}

interface PackingCandidate {
  id: string;
  hqPackingListId: string;
  hqPackingListCode: string;
  factoryName: string;
  displayCode: string;
  selfCode: string;
  grossWeight: number | null;
  netWeight: number | null;
  volumeCm3: number | null;
  products: PackingProduct[];
}

interface BulkProductItem {
  id: string;
  hqPackingListId: string;
  hqPackingListCode: string;
  factoryName: string;
  description: string;
  quantity: number;
  /** 当前数量对应的净重（接口为总净重；拆分时按数量分摊） */
  netWeight: number | null;
  /** 单只净重 = 接口总净重 / 原始数量，仅用于拆分分摊 */
  unitNetWeight: number | null;
}

interface ProductInfoByPack {
  productId: string;
  quantity: number;
}

@Component({
  name: "CreatePackingWizard",
  components: { KeywordSearch },
})
export default class extends Vue {
  @Prop({ default: false }) visible!: boolean;
  @Prop({ required: true }) henganLoadingPlanId!: string;
  @Prop({ default: () => [] }) hqPackingLists!: HqPackingListModel[];

  private step = 1;
  private loading = false;
  private submitting = false;
  private searchTerms: string[] = [];
  private candidates: PackingCandidate[] = [];
  private selectedItems: PackingCandidate[] = [];
  private productCandidates: BulkProductItem[] = [];
  private selectedProducts: BulkProductItem[] = [];
  private containerNumberList: { containerNumber: string }[] = [];
  private form = this.createEmptyForm();
  private productDrawerVisible = false;
  private activeProductItem: PackingCandidate | null = null;

  get dialogVisible(): boolean {
    return this.visible;
  }

  set dialogVisible(val: boolean) {
    this.$emit("update:visible", val);
  }

  get selectedIdSet(): Set<string> {
    return new Set(this.selectedItems.map((item) => item.id));
  }

  get selectedCount(): number {
    return this.selectedItems.length + this.selectedProducts.length;
  }

  get filteredCandidates(): PackingCandidate[] {
    const keywords = this.searchTerms.map((term) => term.toLowerCase());
    return this.candidates.filter((item) => {
      if (this.selectedIdSet.has(item.id)) {
        return false;
      }
      if (!keywords.length) {
        return true;
      }
      return matchesAllKeywords(this.getPackingSearchFields(item), keywords);
    });
  }

  get filteredProductCandidates(): BulkProductItem[] {
    const keywords = this.searchTerms.map((term) => term.toLowerCase());
    return this.productCandidates.filter((item) => {
      if (item.quantity <= 0) {
        return false;
      }
      if (!keywords.length) {
        return true;
      }
      return matchesAllKeywords(this.getProductSearchFields(item), keywords);
    });
  }

  get relatedPackingLists(): HqPackingListModel[] {
    const ids = uniq([
      ...this.selectedItems.map((item) => item.hqPackingListId),
      ...this.selectedProducts.map((item) => item.hqPackingListId),
    ]);
    return this.hqPackingLists.filter((list) => ids.includes(list.id));
  }

  get packingCodeSuffix(): string {
    const code = this.form.customsPackingCode || "";
    if (code.startsWith(this.form.type)) {
      return code.slice(this.form.type.length);
    }
    if (code.startsWith("PLT")) {
      return code.slice(3);
    }
    if (code.startsWith("CTN")) {
      return code.slice(3);
    }
    return code;
  }

  set packingCodeSuffix(value: string) {
    this.form.customsPackingCode = `${this.form.type}${value || ""}`;
  }

  get selectedTotalGross(): string {
    const packingTotal = this.selectedItems.reduce(
      (sum, item) => sum + (Number(item.grossWeight) || 0),
      0
    );
    const productTotal = this.selectedProducts.reduce(
      (sum, item) => sum + (Number(item.netWeight) || 0),
      0
    );
    return (packingTotal + productTotal).toFixed(2);
  }

  get selectedTotalNet(): string {
    const packingTotal = this.selectedItems.reduce(
      (sum, item) => sum + (Number(item.netWeight) || 0),
      0
    );
    const productTotal = this.selectedProducts.reduce(
      (sum, item) => sum + (Number(item.netWeight) || 0),
      0
    );
    return (packingTotal + productTotal).toFixed(2);
  }

  get selectedTotalVolume(): string {
    const total = this.selectedItems.reduce((sum, item) => sum + (Number(item.volumeCm3) || 0), 0);
    return total.toFixed(1);
  }

  get currentVolumeCm3(): number | null {
    const l = Number(this.form.length);
    const w = Number(this.form.width);
    const h = Number(this.form.height);
    if (!l || !w || !h) return null;
    return l * w * h;
  }

  get volumeHintClass(): string {
    if (this.currentVolumeCm3 === null) return "";
    const total = Number(this.selectedTotalVolume);
    return this.currentVolumeCm3 >= total ? "hint-tag--ok" : "hint-tag--warn";
  }

  get volumeHintText(): string {
    if (this.currentVolumeCm3 === null) return "";
    const total = Number(this.selectedTotalVolume);
    const diff = this.currentVolumeCm3 - total;
    if (this.currentVolumeCm3 >= total) {
      return `✓ 已填 ${this.currentVolumeCm3.toFixed(1)}`;
    }
    return `还差 ${Math.abs(diff).toFixed(1)} cm³`;
  }

  get isNetGreaterThanGross(): boolean {
    if (this.form.grossWeight === "" || this.form.netWeight === "") {
      return false;
    }
    const gross = Number(this.form.grossWeight);
    const net = Number(this.form.netWeight);
    if (Number.isNaN(gross) || Number.isNaN(net)) {
      return false;
    }
    return net > gross;
  }

  get createValidationError(): string | null {
    if (!this.form.type) {
      return "请选择新建包装类型";
    }
    if (!this.form.hqPackingListId) {
      return "请选择装箱单";
    }
    if (!this.packingCodeSuffix.trim()) {
      return "请输入主包装编号";
    }

    if (this.form.grossWeight === "") {
      return "请填写毛重";
    }
    if (this.form.netWeight === "") {
      return "请填写净重";
    }
    const gross = Number(this.form.grossWeight);
    const net = Number(this.form.netWeight);
    if (Number.isNaN(gross) || gross < 0) {
      return "请填写有效毛重";
    }
    if (Number.isNaN(net) || net < 0) {
      return "请填写有效净重";
    }
    if (net > gross) {
      return "净重不能大于毛重";
    }
    if (gross < Number(this.selectedTotalGross)) {
      return `毛重不能小于装入内容毛重合计 ${this.selectedTotalGross} kg`;
    }
    if (net < Number(this.selectedTotalNet)) {
      return `净重不能小于装入内容净重合计 ${this.selectedTotalNet} kg`;
    }

    if (this.form.length === "" || this.form.width === "" || this.form.height === "") {
      return "请填写完整尺寸（长宽高）";
    }
    const length = Number(this.form.length);
    const width = Number(this.form.width);
    const height = Number(this.form.height);
    if (
      Number.isNaN(length) ||
      Number.isNaN(width) ||
      Number.isNaN(height) ||
      length <= 0 ||
      width <= 0 ||
      height <= 0
    ) {
      return "请填写有效尺寸（长宽高）";
    }
    const selectedVolume = Number(this.selectedTotalVolume);
    if (selectedVolume > 0 && length * width * height < selectedVolume) {
      return `体积不能小于装入内容体积合计 ${this.selectedTotalVolume} cm³`;
    }
    return null;
  }

  get canCreate(): boolean {
    return this.createValidationError === null;
  }

  get productDrawerTitle(): string {
    if (!this.activeProductItem) {
      return "产品信息";
    }
    return `产品信息 · ${this.activeProductItem.displayCode}`;
  }

  private weightHintClass(type: "gross" | "net"): string {
    if (type === "net" && this.isNetGreaterThanGross) {
      return "hint-tag--warn";
    }
    const value = Number(type === "gross" ? this.form.grossWeight : this.form.netWeight);
    const total = Number(type === "gross" ? this.selectedTotalGross : this.selectedTotalNet);
    if (!value && value !== 0) return "";
    return value >= total ? "hint-tag--ok" : "hint-tag--warn";
  }

  private weightHintText(type: "gross" | "net"): string {
    if (type === "net" && this.isNetGreaterThanGross) {
      return "净重不能大于毛重";
    }
    const value = Number(type === "gross" ? this.form.grossWeight : this.form.netWeight);
    const total = Number(type === "gross" ? this.selectedTotalGross : this.selectedTotalNet);
    if (value >= total) return `✓ 已填 ${value.toFixed(2)}`;
    const diff = total - value;
    return `还差 ${diff.toFixed(2)} kg`;
  }

  private selectType(type: "PLT" | "CTN") {
    const suffix = this.packingCodeSuffix;
    this.form.type = type;
    this.form.customsPackingCode = `${type}${suffix}`;
  }

  private inputStateClass(type: "gross" | "net"): string {
    if (type === "net" && this.isNetGreaterThanGross) {
      return "input--warn";
    }
    const raw = type === "gross" ? this.form.grossWeight : this.form.netWeight;
    if (raw === "") return "";
    const value = Number(raw);
    const total = Number(type === "gross" ? this.selectedTotalGross : this.selectedTotalNet);
    return value >= total ? "input--ok" : "input--warn";
  }

  @Watch("visible")
  onVisibleChange(val: boolean) {
    if (val) {
      this.resetWizard();
      this.buildCandidates();
    }
  }

  private resetWizard() {
    this.step = 1;
    this.searchTerms = [];
    (this.$refs.keywordSearch as Vue & { clear?: () => void })?.clear?.();
    this.selectedItems = [];
    this.selectedProducts = [];
    this.containerNumberList = [];
    this.productDrawerVisible = false;
    this.activeProductItem = null;
    this.form = this.createEmptyForm();
  }

  private createEmptyForm() {
    return {
      type: "PLT",
      hqPackingListId: "",
      customsPackingCode: "",
      grossWeight: "",
      netWeight: "",
      length: "",
      width: "",
      height: "",
      containerNumber: "",
    };
  }

  private buildCandidates() {
    this.loading = true;
    const result: PackingCandidate[] = [];
    this.hqPackingLists.forEach((list) => {
      (list.customsPackingSet || []).forEach((packing) => {
        result.push(this.toCandidate(packing, list));
      });
    });
    this.candidates = result;
    this.buildBulkProducts();
    this.loading = false;
  }

  private buildBulkProducts() {
    const result: BulkProductItem[] = [];
    this.hqPackingLists.forEach((list) => {
      (list.unpackedProductInfos || []).forEach((product) => {
        const quantity = Number(product.totalNumberPcs) || 0;
        if (!product.id || quantity <= 0) {
          return;
        }
        // 接口 netWeight 已是总净重（数量×单只），展示时直接使用，拆分时再按数量分摊
        const totalNetWeight =
          product.netWeight === null || product.netWeight === undefined
            ? null
            : Number(product.netWeight);
        result.push({
          id: product.id,
          hqPackingListId: product.parentId || list.id,
          hqPackingListCode: list.code,
          factoryName: list.factoryName || "-",
          description: product.description || "-",
          quantity,
          netWeight: totalNetWeight,
          unitNetWeight:
            totalNetWeight === null ? null : totalNetWeight / quantity,
        });
      });
    });
    this.productCandidates = result;
    this.selectedProducts = [];
  }

  private toCandidate(
    packing: CustomsPackingModel,
    list: HqPackingListModel
  ): PackingCandidate {
    const packingAny = packing as CustomsPackingModel & {
      grossWeight?: number;
      netWeight?: number;
      length?: number;
      width?: number;
      height?: number;
    };
    const grossWeight =
      packingAny.grossWeight ?? packing.packingWeight?.grossWeight ?? null;
    const netWeight =
      packingAny.netWeight ?? packing.packingWeight?.netWeight ?? null;
    const length =
      packingAny.length ?? packing.packingVolume?.length ?? null;
    const width = packingAny.width ?? packing.packingVolume?.width ?? null;
    const height =
      packingAny.height ?? packing.packingVolume?.height ?? null;
    const volumeCm3 =
      length && width && height ? Number((length * width * height).toFixed(1)) : null;
    const typeCode = packing.code?.typeCode || "";
    const selfCode = packing.code?.selfCode || "";
    const serialNumber = packing.code?.serialNumber || "";

    return {
      id: packing.id,
      hqPackingListId: list.id,
      hqPackingListCode: list.code,
      factoryName: list.factoryName || "-",
      selfCode,
      displayCode: `${typeCode} ${selfCode} ${serialNumber}`.trim(),
      grossWeight,
      netWeight,
      volumeCm3,
      products: this.extractProducts(packing),
    };
  }

  private extractProducts(packing: CustomsPackingModel): PackingProduct[] {
    const products: PackingProduct[] = [];
    (packing.secondaryPackingSet || []).forEach((secondaryPacking) => {
      (secondaryPacking.packingInfos || []).forEach((packingInfo) => {
        (packingInfo.productInfos || []).forEach((product) => {
          products.push({
            description: product.description || "-",
            declarationDescription: product.declarationDescription || "-",
            guestModel: product.guestModel || "-",
            hqOrJrNumber: product.hqOrJrNumber || "-",
            oeNumber: product.oeNumber || "-",
            refNumber: product.refNumber || "-",
            hqTransactionNumber: product.hqTransactionNumber || "-",
            totalNumberPcs: product.totalNumberPcs || 0,
            purchaseOrderNumber: product.purchaseOrderNumber || "-",
          });
        });
      });
    });
    return products;
  }

  private showProductInfo(item: PackingCandidate) {
    this.activeProductItem = item;
    this.productDrawerVisible = true;
  }

  private addToSelected(item: PackingCandidate) {
    if (this.selectedIdSet.has(item.id)) {
      return;
    }
    this.selectedItems = [...this.selectedItems, item];
  }

  private removeFromSelected(item: PackingCandidate) {
    this.selectedItems = this.selectedItems.filter((selected) => selected.id !== item.id);
  }

  private async addProductToSelected(item: BulkProductItem) {
    if (item.quantity <= 0) {
      return;
    }
    try {
      const result = await MessageBox.prompt(
        `可选择数量：1 - ${item.quantity}`,
        `选择数量 · ${item.description}`,
        {
          confirmButtonText: "确定",
          cancelButtonText: "取消",
          inputValue: String(item.quantity),
          inputPattern: /^[1-9]\d*$/,
          inputErrorMessage: "请输入正整数",
          inputValidator: (val: string) => {
            const qty = Number(val);
            if (!Number.isInteger(qty) || qty < 1) {
              return "请输入正整数";
            }
            if (qty > item.quantity) {
              return `不能超过剩余数量 ${item.quantity}`;
            }
            return true;
          },
        }
      );
      if (typeof result === "string") {
        return;
      }
      this.applySelectProduct(item.id, Number(result.value));
    } catch {
      // cancelled
    }
  }

  private applySelectProduct(productId: string, qty: number) {
    const candidate = this.productCandidates.find((item) => item.id === productId);
    if (!candidate || qty <= 0 || qty > candidate.quantity) {
      return;
    }
    const unit = candidate.unitNetWeight;
    candidate.quantity -= qty;
    candidate.netWeight = unit === null ? null : unit * candidate.quantity;

    const existing = this.selectedProducts.find((item) => item.id === productId);
    if (existing) {
      existing.quantity += qty;
      existing.netWeight = unit === null ? null : unit * existing.quantity;
      this.selectedProducts = [...this.selectedProducts];
    } else {
      this.selectedProducts = [
        ...this.selectedProducts,
        {
          ...candidate,
          quantity: qty,
          netWeight: unit === null ? null : unit * qty,
        },
      ];
    }
    this.productCandidates = [...this.productCandidates];
  }

  private removeProductFromSelected(item: BulkProductItem) {
    const selected = this.selectedProducts.find((product) => product.id === item.id);
    if (!selected) {
      return;
    }
    const unit = selected.unitNetWeight;
    const candidate = this.productCandidates.find((product) => product.id === item.id);
    if (candidate) {
      candidate.quantity += selected.quantity;
      candidate.netWeight = unit === null ? null : unit * candidate.quantity;
      this.productCandidates = [...this.productCandidates];
    } else {
      this.productCandidates = [
        ...this.productCandidates,
        {
          ...selected,
        },
      ];
    }
    this.selectedProducts = this.selectedProducts.filter((product) => product.id !== item.id);
  }

  private formatWeight(value: number | null): string {
    if (value === null || value === undefined || Number.isNaN(Number(value))) {
      return "-";
    }
    return Number(value).toFixed(2);
  }

  private productIdSuffix(id: string): string {
    const source = id || "";
    return source.slice(-4);
  }

  private formatVolume(value: number | null): string {
    if (value === null || value === undefined || Number.isNaN(Number(value))) {
      return "-";
    }
    return `${Number(value).toFixed(1)} cm³`;
  }

  private getPackingSearchFields(item: PackingCandidate): string[] {
    return [item.factoryName, item.selfCode, item.displayCode];
  }

  private getProductSearchFields(item: BulkProductItem): string[] {
    return [item.factoryName, item.hqPackingListCode, item.description];
  }

  private highlightMatch(text: string): string {
    return highlightKeywords(text, this.searchTerms);
  }

  private async goToFormStep() {
    if (this.selectedCount === 0) {
      Message.error({ message: "请至少选择一个包装或散装产品" });
      return;
    }
    try {
      if (this.selectedItems.length > 0) {
        const customsPackingIds = this.selectedItems.map((item) => item.id);
        const res: any = await postContainerNumber(uniq(customsPackingIds));
        this.containerNumberList = res || [];
      } else {
        this.containerNumberList = [];
      }
      const relatedLists = this.relatedPackingLists;
      this.form.hqPackingListId =
        relatedLists.length === 1 ? relatedLists[0].id : "";
      this.form.customsPackingCode = this.form.type || "PLT";
      this.step = 2;
    } catch (err) {
      console.log(err);
    }
  }

  private async handleCreate() {
    const error = this.createValidationError;
    if (error) {
      Message.error({ message: error });
      return;
    }

    const productInfos: ProductInfoByPack[] = this.selectedProducts.map((item) => ({
      productId: item.id,
      quantity: item.quantity,
    }));

    const request = {
      ...cloneDeep(this.form),
      customsPackingIds: this.selectedItems.map((item) => item.id),
      productInfos,
    };

    this.submitting = true;
    try {
      await packCustomsPacking(this.henganLoadingPlanId, request);
      Message.success({ message: "新建包装成功" });
      this.dialogVisible = false;
      this.$emit("success");
    } catch (err) {
      console.log(err);
    } finally {
      this.submitting = false;
    }
  }

  private handleClose() {
    this.dialogVisible = false;
  }
}
